import { useMemo } from "react";
import { CARD_CLASS } from "../styles";
import type { DynamicsData } from "../types";
import { formatPrice } from "../utils";

type Props = {
  title: string;
  data: DynamicsData;
  fullCellHeatmap?: boolean;
  alignRight?: boolean;
};

export default function DynamicsTableCard({ title, data, fullCellHeatmap = false, alignRight = false }: Props) {
  const hasData = data.dates.length > 0 && data.rows.length > 0;
  const minMax = useMemo(() => {
    const values = data.rows
      .flatMap((row) => row.values)
      .filter((value): value is number => typeof value === "number");
    if (!values.length) return { min: 0, max: 0 };
    return { min: Math.min(...values), max: Math.max(...values) };
  }, [data]);

  const getCellStyle = (value: number | null) => {
    if (value === null || Number.isNaN(value)) {
      return { className: "bg-slate-50 text-slate-400", style: undefined };
    }
    if (minMax.max <= minMax.min) {
      return { className: "bg-slate-50 text-slate-600", style: undefined };
    }
    const ratio = (value - minMax.min) / (minMax.max - minMax.min);
    // green (low) -> yellow (mid) -> red (high)
    const hue = 120 - ratio * 120;
    const backgroundColor = `hsla(${hue.toFixed(0)}, 70%, 82%, 0.9)`;
    return { className: "text-slate-800", style: { backgroundColor } };
  };

  return (
    <section className={CARD_CLASS}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <p className="text-xs text-slate-500">Сравнение по каждому объекту</p>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        {hasData ? (
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs font-semibold uppercase text-slate-400">
                <th className="sticky left-0 z-10 min-w-[220px] bg-slate-50 px-4 py-3 text-left">Отель</th>
                {data.dates.map((date) => (
                  <th key={date} className="px-4 py-3 text-center font-semibold text-slate-500">
                    {date}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr
                  key={row.name}
                  className={[
                    "border-b border-slate-100",
                    row.isClient ? "bg-yellow-50" : "bg-white",
                  ].join(" ")}
                >
                  <td
                    className={[
                      "sticky left-0 z-10 px-4 py-3 text-left font-semibold",
                      row.isClient ? "bg-yellow-50 text-yellow-800" : "bg-white text-slate-700",
                    ].join(" ")}
                  >
                    {row.name}
                  </td>
                  {row.values.map((value, index) => {
                    const cell = getCellStyle(value);
                    const tdStyle = row.isClient || !fullCellHeatmap || !cell.style ? undefined : cell.style;
                    const spanClass = fullCellHeatmap
                      ? cell.style
                        ? "text-slate-800"
                        : cell.className
                      : cell.className;
                    const spanRadius = fullCellHeatmap && !row.isClient ? "" : "rounded-lg";
                    return (
                      <td
                        key={`${row.name}-${index}`}
                        className={[
                          "px-4 py-3 text-center font-semibold",
                          row.isClient ? "text-slate-900" : "",
                          alignRight ? "text-right" : "text-center",
                        ].join(" ")}
                        style={tdStyle}
                      >
                        <span
                          className={[
                            "inline-flex min-w-[70px] px-2 py-1",
                            row.isClient ? "rounded-lg bg-yellow-200 text-yellow-900" : "",
                            fullCellHeatmap && !row.isClient ? "text-slate-800" : spanRadius,
                            row.isClient ? "" : spanClass,
                            alignRight ? "justify-end" : "justify-center",
                          ].join(" ")}
                          style={row.isClient || fullCellHeatmap ? undefined : cell.style}
                        >
                          {formatPrice(value)}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="grid h-[200px] place-items-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 text-sm text-slate-500">
            Нет данных для отображения
          </div>
        )}
      </div>
    </section>
  );
}
